import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Renderer2,
} from '@angular/core';
import { OrganizationRequest } from '../../models/organization-request.interface';
import { SuccessResponse } from '../../models/responses.interface';
import { OrganizationService } from '../../services/organization.service';
import { Organization } from '../../models/organization.interface';
import {
  OrganizationResponse,
  GenerateSDKKeysResponse,
  GenerateAPIKeyResponse,
} from '../../models/organization-response.interface';
import { BridgePayAuthService } from '../../services/bridgepay-auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kt-organization-details',
  templateUrl: './organization-details.component.html',
  styleUrls: ['./organization-details.component.scss'],
})
export class OrganizationDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() organization: Organization;
  isLoading = false;
  organizationExists = false;
  organizationData: OrganizationResponse;

  // SDK Keys management
  showPrivateKey = false;
  privateKeyValue = '';
  keysCopied = false;
  timeoutProgress = 100;
  remainingTime = 60;
  isGeneratingKeys = false;

  private destroy$ = new Subject<void>();
  private keyDisplayTimer$ = new Subject<void>();

  constructor(
    private organizationService: OrganizationService,
    private _snackBar: MatSnackBar,
    private cd: ChangeDetectorRef,
    private bridgePayAuth: BridgePayAuthService,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    if (this.organization) {
      this.loadOrganization();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organization && !changes.organization.firstChange) {
      // Reset state when organization changes
      this.organizationExists = false;
      this.organizationData = null;
      this.clearPrivateKey();

      if (this.organization) {
        this.loadOrganization();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.keyDisplayTimer$.next();
    this.keyDisplayTimer$.complete();
    this.clearPrivateKey();
  }

  private loadOrganization(): void {
    this.isLoading = true;
    if (this.organization.bridgepayApiKey) {
      // Initialize BridgePay auth with the new API key
      this.bridgePayAuth
        .initializeAuth(this.organization.bridgepayApiKey, this.organization.id)
        .subscribe();
      this.organizationService.getDefaultOrganization(this.organization.id).subscribe(
        (response: SuccessResponse<OrganizationResponse>) => {
          if (response.data) {
            this.organizationExists = true;
            this.organizationData = response.data;

            // Handle new API key format
            if (response.data.api_key) {
              this.privateKeyValue = response.data.api_key;
              this.showPrivateKey = true;
              this.startKeyDisplayTimer();

              this._snackBar.open(
                "API key is visible! Copy it now - it won't be shown again!",
                'OK',
                {
                  duration: 10000,
                  panelClass: ['warning-snackbar'],
                },
              );
            }
            // Fallback for legacy SDK keys
            else if (response.data.sdk_private_key) {
              this.privateKeyValue = response.data.sdk_private_key;
              this.showPrivateKey = true;
              this.startKeyDisplayTimer();

              this._snackBar.open(
                "Private key is visible! Copy it now - it won't be shown again!",
                'OK',
                {
                  duration: 10000,
                  panelClass: ['warning-snackbar'],
                },
              );
            }
          } else {
            this.organizationExists = false;
            this.organizationData = null;
          }
          this.isLoading = false;
          this.cd.detectChanges();
        },
        () => {
          this.organizationExists = false;
          this.organizationData = null;
          this.isLoading = false;
          this.cd.detectChanges();
        },
        () => {
          this.isLoading = false;
          this.cd.detectChanges();
        },
      );
    } else {
      this.organizationExists = false;
      this.organizationData = null;
      this.isLoading = false;
      this.cd.detectChanges();
    }
  }

  createOrganization() {
    const payload: OrganizationRequest = {
      name: this.organization.name,
      knostos_company_id: this.organization.id,
      reply_to_email: this.organization.email,
    };

    this.isLoading = true;

    this.organizationService.createOrganization(payload).subscribe(
      (response: SuccessResponse<OrganizationResponse>) => {
        this.organizationExists = true;
        this.organizationData = response.data;

        // Check if API key was generated during creation
        if (response.data.api_key) {
          // Show the API key immediately after creation
          this.privateKeyValue = response.data.api_key;
          this.showPrivateKey = true;
          this.startKeyDisplayTimer();

          // Initialize BridgePay auth
          this.bridgePayAuth
            .initializeAuth(response.data.api_key, this.organization.id)
            .subscribe();

          this._snackBar.open(
            "Organization created! Copy your API key now - it won't be shown again!",
            'OK',
            {
              duration: 10000,
              panelClass: ['warning-snackbar'],
            },
          );
        }
        // Fallback for legacy SDK keys
        else if (response.data.sdk_public_key && response.data.sdk_private_key) {
          this.privateKeyValue = response.data.sdk_private_key;
          this.showPrivateKey = true;
          this.startKeyDisplayTimer();

          this._snackBar.open(
            "Organization created! Copy your private key now - it won't be shown again!",
            'OK',
            {
              duration: 10000,
              panelClass: ['warning-snackbar'],
            },
          );
        } else {
          this._snackBar.open('Organization created successfully', 'OK', {
            duration: 3000,
            panelClass: ['success-snackbar'],
          });
        }

        this.isLoading = false;
        this.cd.detectChanges();
      },
      () => {
        this.isLoading = false;
        this.cd.detectChanges();
      },
      () => {
        this.isLoading = false;
        this.cd.detectChanges();
      },
    );
  }

  generateSDKKeys(): void {
    if (
      !confirm(
        'Are you sure you want to generate new SDK keys? This will invalidate any existing keys.',
      )
    ) {
      return;
    }

    this.isGeneratingKeys = true;
    this.organizationService.generateSDKKeys(this.organization.id).subscribe(
      (response: SuccessResponse<GenerateSDKKeysResponse>) => {
        if (response.data) {
          this.privateKeyValue = response.data.sdk_private_key;
          this.showPrivateKey = true;
          this.startKeyDisplayTimer();

          // Update organization data with new public key
          if (this.organizationData) {
            this.organizationData.sdk_public_key = response.data.sdk_public_key;
          }

          this._snackBar.open('SDK keys generated successfully. Copy the private key now!', 'OK', {
            duration: 5000,
            panelClass: ['warning-snackbar'],
          });
        }
        this.isGeneratingKeys = false;
        this.cd.detectChanges();
      },
      () => {
        this._snackBar.open('Failed to generate SDK keys', 'OK', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });
        this.isGeneratingKeys = false;
        this.cd.detectChanges();
      },
    );
  }

  /**
   * Generate new API key using the new Bearer auth format
   */
  generateAPIKey(): void {
    if (
      !confirm(
        'Are you sure you want to generate a new API key? This will invalidate any existing keys.',
      )
    ) {
      return;
    }

    this.isGeneratingKeys = true;
    const keyName = `${this.organization.name} API Key`;

    this.organizationService.generateAPIKey(this.organization.id, keyName).subscribe(
      (response: SuccessResponse<GenerateAPIKeyResponse>) => {
        if (response.data) {
          this.privateKeyValue = response.data.api_key;
          this.showPrivateKey = true;
          this.startKeyDisplayTimer();

          // Update organization data with new API key
          if (this.organizationData) {
            this.organizationData.api_key = response.data.api_key;
          }

          // Initialize BridgePay auth with the new API key
          this.bridgePayAuth.updateApiKey(response.data.api_key, this.organization.id).subscribe();

          this._snackBar.open(
            "API key generated successfully. Copy it now - it won't be shown again!",
            'OK',
            {
              duration: 10000,
              panelClass: ['warning-snackbar'],
            },
          );
        }
        this.isGeneratingKeys = false;
        this.cd.detectChanges();
      },
      () => {
        this._snackBar.open('Failed to generate API key', 'OK', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });
        this.isGeneratingKeys = false;
        this.cd.detectChanges();
      },
    );
  }

  private startKeyDisplayTimer(): void {
    this.keyDisplayTimer$.next(); // Cancel any existing timer
    this.remainingTime = 60;
    this.timeoutProgress = 100;

    interval(1000)
      .pipe(takeUntil(this.keyDisplayTimer$))
      .subscribe(() => {
        this.remainingTime--;
        this.timeoutProgress = (this.remainingTime / 60) * 100;

        if (this.remainingTime <= 0) {
          this.clearPrivateKey();
        }
        this.cd.detectChanges();
      });
  }

  clearPrivateKey(): void {
    this.privateKeyValue = '';
    this.showPrivateKey = false;
    this.keysCopied = false;
    this.keyDisplayTimer$.next();
  }

  copyToClipboard(value: string, keyType = 'key'): void {
    if (!value) return;

    navigator.clipboard
      .writeText(value)
      .then(() => {
        this._snackBar.open(`${keyType} copied to clipboard`, 'OK', {
          duration: 2000,
          panelClass: ['success-snackbar'],
        });
      })
      .catch(() => {
        this._snackBar.open('Failed to copy to clipboard', 'OK', {
          duration: 2000,
          panelClass: ['error-snackbar'],
        });
      });
  }

  copyPrivateKey(): void {
    if (!this.privateKeyValue) return;

    navigator.clipboard.writeText(this.privateKeyValue).then(() => {
      this.keysCopied = true;
      this._snackBar.open('Private key copied to clipboard. Store it securely!', 'OK', {
        duration: 3000,
        panelClass: ['success-snackbar'],
      });

      // Clear after 5 seconds if copied
      setTimeout(() => {
        this.clearPrivateKey();
      }, 5000);
    });
  }

  downloadKeys(): void {
    if (!this.organizationData || !this.privateKeyValue) return;

    const keysData = {
      organization: this.organization.name,
      environment: 'production', // You might want to get this from config
      generated_at: new Date().toISOString(),
      sdk_public_key: this.organizationData.sdk_public_key,
      sdk_private_key: this.privateKeyValue,
      warning: 'Keep this file secure. The private key will not be shown again.',
    };

    const dataStr = JSON.stringify(keysData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `bridgepay-sdk-keys-${this.organization.name
      .toLowerCase()
      .replace(/\s+/g, '-')}-${Date.now()}.json`;

    const linkElement = this.renderer.createElement('a');
    this.renderer.setAttribute(linkElement, 'href', dataUri);
    this.renderer.setAttribute(linkElement, 'download', exportFileDefaultName);
    this.renderer.setStyle(linkElement, 'display', 'none');
    this.renderer.appendChild(document.body, linkElement);
    this.renderer.selectRootElement(linkElement).click();

    this._snackBar.open('Keys downloaded. Store the file securely!', 'OK', {
      duration: 3000,
      panelClass: ['success-snackbar'],
    });
  }
}
